import React, { useState } from 'react';
import type { PersonRef } from '../domain/people';
import { useMyId } from '../hooks/useSession';
import { fullName, isMe } from '../utils/people';
import BlockDialog from './BlockDialog';
import ReportDialog from './ReportDialog';
import UserAvatar from './UserAvatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Ban, Flag } from 'lucide-react';

/**
 * A person, opened by tapping their name (017 research D20, Q1): photo,
 * name, and **Block** and **Report** — never for myself. There is no profile
 * page; the sheet takes the `PersonRef` the screen already has.
 */
export default function PersonDialog({
  person,
  open,
  onOpenChange,
  onBlocked,
}: {
  person: PersonRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** After a block, so the screen can reload what the block took away (FR-032). */
  onBlocked?: () => void;
}) {
  const myId = useMyId();
  const [action, setAction] = useState<'block' | 'report' | null>(null);
  const mine = isMe(person, myId);
  const name = fullName(person);

  const start = (next: 'block' | 'report') => {
    onOpenChange(false);
    setAction(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-3xl border-none bg-background sm:max-w-sm">
          <DialogHeader className="items-center text-center">
            <UserAvatar person={person} className="size-20" textClassName="text-2xl" />
            <DialogTitle className="text-xl">{name}</DialogTitle>
            <DialogDescription className="sr-only">
              {mine ? 'This is you.' : `What you can do about ${name}.`}
            </DialogDescription>
          </DialogHeader>
          {!mine && (
            <DialogFooter className="gap-2 sm:justify-center">
              <Button variant="secondary" className="rounded-full" onClick={() => start('report')}>
                <Flag className="size-4" aria-hidden="true" />
                Report
              </Button>
              <Button variant="destructive" className="rounded-full" onClick={() => start('block')}>
                <Ban className="size-4" aria-hidden="true" />
                Block
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      <BlockDialog
        person={person}
        open={action === 'block'}
        onOpenChange={(o) => !o && setAction(null)}
        onDone={() => onBlocked?.()}
      />
      <ReportDialog
        target={{ kind: 'person', id: person.id }}
        open={action === 'report'}
        onOpenChange={(o) => !o && setAction(null)}
      />
    </>
  );
}

/**
 * A person's name that opens their `PersonDialog`. My own name is plain
 * text: there's nothing to do about myself.
 */
export function PersonName({
  person,
  className,
  onBlocked,
}: {
  person: PersonRef;
  className?: string;
  onBlocked?: () => void;
}) {
  const myId = useMyId();
  const [open, setOpen] = useState(false);
  const name = fullName(person);
  if (isMe(person, myId)) return <span className={className}>{name}</span>;
  return (
    <>
      <button
        type="button"
        className={`text-left hover:text-primary hover:underline ${className ?? ''}`}
        onClick={() => setOpen(true)}
      >
        {name}
      </button>
      <PersonDialog person={person} open={open} onOpenChange={setOpen} onBlocked={onBlocked} />
    </>
  );
}
